import type { StoreAdmin, StoreRole } from "@hobbyshopify/backend-client";

// The confirmed matrix (Owner / Manager / Staff), bench issue HB-I052. Mirrored
// one to one by AdminStoreRole.swift in HobbyScan-iOS — keep names aligned.

export type Permission =
  | "scans"
  | "carts"
  | "inventoryEdit"
  | "reports"
  | "manageStaff"
  | "manageManagersAndOwners"
  | "shopSettings"
  | "payments"
  | "hostLive"
  | "hostEvents"
  | "takeTables"
  | "requestCapabilities";

export function can(role: StoreRole, permission: Permission): boolean {
  switch (permission) {
    case "scans":
    case "carts":
      return true;
    case "inventoryEdit":
    case "reports":
    case "manageStaff":
    case "hostLive":
    case "hostEvents":
    case "takeTables":
    case "requestCapabilities":
      return role === "OWNER" || role === "MANAGER";
    case "manageManagersAndOwners":
    case "shopSettings":
    case "payments":
      return role === "OWNER";
  }
}

/** Roles `callerRole` may assign when adding or promoting an admin. */
export function assignableRoles(callerRole: StoreRole): StoreRole[] {
  switch (callerRole) {
    case "OWNER":
      return ["OWNER", "MANAGER", "STAFF"];
    case "MANAGER":
      return ["STAFF"];
    case "STAFF":
      return [];
  }
}

/**
 * Whether `callerRole` may remove `member`. Nobody removes themselves; a
 * Manager may only remove Staff; the last remaining Owner can never be
 * removed, by anyone.
 */
export function canRemove(
  callerRole: StoreRole,
  member: Pick<StoreAdmin, "role" | "isSelf">,
  ownerCount: number,
): boolean {
  if (member.isSelf) return false;
  if (member.role === "OWNER" && ownerCount <= 1) return false;
  switch (callerRole) {
    case "OWNER":
      return true;
    case "MANAGER":
      return member.role === "STAFF";
    case "STAFF":
      return false;
  }
}

/** Only an Owner changes roles, and never their own. */
export function canChangeRole(callerRole: StoreRole, member: Pick<StoreAdmin, "isSelf">): boolean {
  return callerRole === "OWNER" && !member.isSelf;
}

export function roleLabel(role: StoreRole): string {
  switch (role) {
    case "OWNER":
      return "Owner";
    case "MANAGER":
      return "Manager";
    case "STAFF":
      return "Staff";
  }
}
